<template>
  <div class="slo-input-wrapper">
    <label
      ref="root"
      :class="{
        'slo-input': true,
        [`size-${size}`]: size,
        [`variant-${variant}`]: variant,
        [`type-${type}`]: true,
        checked: checked,
        indeterminate: indeterminate,
        disabled: disabled || fieldState.disabled,
        'focus-within': fieldState.focusWithin,
        'slo-input--error': error,
        'slo-input--has-icon': $slots.start || $slots.end
      }"
      v-stack-x:2xs
    >
      <slot name="start" />
      <input
        ref="input"
        :type="type"
        v-bind="$attrs"
        :value="value"
        :checked="checked"
        :indeterminate.prop="indeterminate"
        :disabled="disabled || fieldState.disabled"
        @blur="handleBlur"
        @keydown.stop="handleKeydown"
        @input.stop="handleInput"
      />
      <slot name="end" />
    </label>
    <span v-if="errorText" v-html="errorText" class="slo-input--error-message" />
  </div>
</template>

<script>
import t from '../../js/main/i18n';

export default {
  name: 'SloInput',
  inheritAttrs: false,
  inject: {
    fieldState: {name: 'fieldState', default: {}}
  },
  props: {
    size: {type: String},
    disabled: {type: Boolean, default: false},
    variant: {type: String},
    type: {type: String, default: 'text'},
    value: {type: [String, Number, Boolean, Array, Object], default: null},
    checked: {type: Boolean, default: false},
    indeterminate: {type: Boolean, default: false},
    error: {type: [Boolean, String], default: false}
  },
  computed: {
    errorText() {
      if (typeof this.error === 'boolean') return false;
      return t(this.error);
    }
  },
  methods: {
    setFocus() {
      this.$refs.input.focus();
    },
    handleBlur(event) {
      this.$emit('blur', event);
    },
    handleKeydown(event) {
      if (event.key === 'Enter') return this.$emit('enter', event);
      if (event.key === 'Tab') return this.$emit('tab', event);
      if (event.key === 'Backspace') return this.$emit('backspace', event);
    },
    handleInput(event) {
      const type = this.type;
      if (type === 'checkbox' || type === 'radio') {
        this.$emit('input', event.target.checked);
      } else {
        this.$emit('input', event.target.value);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/system';

.slo-input {
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  font-weight: normal;
  border-radius: 4px;

  > .slo-icon:first-child {
    margin-left: 1em;
    margin-right: -8px;
  }

  > .slo-icon:last-child {
    margin-right: 1em;
    margin-left: -8px;
  }
}

.slo-input.type-text,
.slo-input.type-number,
.slo-input.type-email,
.slo-input.type-password,
.slo-input.type-search,
.slo-input.type-date,
.slo-input.type-tel {
  height: auto;
  background: $color-white;
  border: 1px solid $color-gray-400;

  input {
    flex: 1;
    width: 100%;
    border: none;
    box-shadow: none;
    margin: 0;
    padding: 12px 16px;
    height: 48px;
    font-weight: inherit;
    border-radius: 4px;
    transition: none;

    @include text('base');
    color: $color-gray-900;

    &::placeholder {
      color: $color-gray-700;
    }
  }

  &.variant-inset {
    height: auto;
    background: $color-gray-200;
    border-radius: 4px;

    input {
      background: inherit;
      border-radius: 4px;
    }
  }

  &:hover {
    border-color: $color-gray-600;
  }

  &:focus-within {
    border-color: $color-blue-400;
    outline: 1px solid transparent;
    box-shadow: 0 0 0 1px $color-blue-400;
  }

  &.slo-input--error {
    border-color: $color-red-400;
    outline: 1px solid transparent;

    &:focus-within {
      border-color: $color-red-400;
      outline: 1px solid transparent;
      box-shadow: 0 0 0 1px $color-red-400;
    }
  }
}

.slo-input--error-message {
  color: $color-red-400;
  font-weight: 400;
}

.slo-input.focus-within {
  border-color: $color-blue-400 !important;
  outline: 1px solid transparent !important;
  box-shadow: 0 0 0 1px $color-blue-400 !important;
}

.slo-input.type-checkbox {
  @include text('base');
  color: $color-gray-900;
  cursor: pointer;

  &.disabled {
    color: $color-gray-700;
    pointer-events: none;

    > input {
      border-color: $color-gray-400;
    }
  }
}

.slo-input.type-checkbox > input {
  position: relative;
  margin: 0;
  width: 1.4rem;
  height: 1.4rem;
  border: 1px solid $color-gray-700;
  border-radius: 3px;
  appearance: none;
}

.slo-input.type-checkbox.size-large > input {
  width: 2rem;
  height: 2rem;
}

.slo-input.type-checkbox > input::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.8em;
  height: 0.8em;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.2) rotate(-45deg);
  transition: 120ms ease-out;
  transition-property: transform, opacity;
}

.slo-input.type-checkbox > input:checked,
.slo-input.type-checkbox.checked > input {
  background-color: $color-blue-400;
  border-color: $color-blue-400;
}

.slo-input.type-checkbox > input:checked::before,
.slo-input.type-checkbox.checked > input::before {
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white" width="18px" height="18px"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" /></svg>');
  background-position: center;
  background-size: contain;
  opacity: 1;
  transform: translate(-50%, -50%) scale(0.8) rotate(0deg);
}

.slo-input.type-checkbox > input:indeterminate::before,
.slo-input.type-checkbox.indeterminate > input::before {
  background-image: none;
  border-bottom: 2px solid $color-blue-400;
  opacity: 1;
  width: 0.7em;
  height: 1px;
  transform: translate(-50%, -50%) scale(0.8) rotate(-180deg);
}
</style>
